<template>
  <module-page v-bind="pageParams">
    <v-layout row wrap>
      <v-flex xs12>
        <v-tabs class="page__tabs" hide-slider>
          <v-tab
            v-for="(tab, i) in pageParams.tabs"
            :to="tab.value"
            :key="i"
            active-class="white--text financial--background"
          >
            <span
              :class="
                hasPayableLateSlips && tab.icon ? 'enterprise--color' : ''
              "
            >
              {{ !mobile ? tab.title : tab.titleMobile }}
              <i
                v-if="hasPayableLateSlips && tab.icon"
                :class="`${tab.icon} side-text--icon`"
              />
            </span>
          </v-tab>
        </v-tabs>
        <div v-if="!mobile" class="to-expire-payment-slip__description">
          <p>{{ descriptionFirstLine }}</p>
          <p>
            {{ descriptionSecondLine }}
            <router-link
              v-if="ticketCrmEnabled"
              :to="{ name: 'ticketForm' }"
              tag="a"
              class="payment-slip-link"
            >
              <u>{{ descriptionLink }}</u>
            </router-link>
          </p>
        </div>
        <div v-else class="to-expire-payment-slip__description">
          <p>
            {{ descriptionFirstLine }} {{ descriptionSecondLine }}
            <router-link
              v-if="ticketCrmEnabled"
              :to="{ name: 'ticketForm' }"
              tag="a"
              class="payment-slip-link"
            >
              <u>{{ descriptionLink }}</u>
            </router-link>
          </p>
        </div>
        <screen-loading v-if="isLoadingPaymentSlips"></screen-loading>
        <div v-else-if="!hasPaymentSlipError">
          <template v-if="debitBlock">
            <empty-state-card icon="alert">
              <div slot="message" class="error--color">
                <p class="waiting-state__title">
                  <span>
                    <b>{{ $t('pages.paymentSlip.operationNotAllowed') }}</b>
                  </span>
                </p>
                <p class="waiting-state__text">
                  <span>{{
                    $t('pages.paymentSlip.partialSlipWarningSubTitle')
                  }}</span>
                </p>
                <p class="waiting-state__text">
                  <router-link
                    v-if="ticketCrmEnabled"
                    class="error--color font-weight-bold"
                    :to="{ name: 'ticketForm' }"
                    tag="a"
                  >
                    <u>{{ $t('pages.paymentSlip.supportLink') }}</u>
                  </router-link>
                </p>
              </div>
            </empty-state-card>
          </template>
          <template
            v-else-if="
              !toExpirePaymentSlips || toExpirePaymentSlips.length === 0
            "
          >
            <empty-state-card :icon="enums.paymentSlipWaitingState.icon">
              <div slot="message" v-html="emptyPaymentSlipsHtml" />
            </empty-state-card>
          </template>
          <template v-else>
            <span v-for="(contract, i) in allowedContracts" :key="i">
              <v-card
                v-if="
                  toExpireSlipsByContract[contract.id] &&
                  toExpireSlipsByContract[contract.id].length > 0
                "
                class="mt-2 mb-2"
              >
                <v-card-title class="pt-headline financial--color">{{
                  getContractTitle(contract)
                }}</v-card-title>
                <v-card-text>
                  <v-data-table
                    v-if="loaded"
                    :headers="headers"
                    :items="toExpireSlipsByContract[contract.id]"
                    item-key="uId"
                    hide-actions
                    :no-data-text="pageParams.emptyState.toExpire"
                    sort-icon="bexsicon-down"
                  >
                    <template slot="items" slot-scope="props">
                      <td class="text-xs-center">
                        {{ formatDate(props.item.expirationDate) }}
                      </td>
                      <td class="text-xs-center bold--text">
                        <v-progress-circular
                          color="green"
                          indeterminate
                          size="25"
                          :width="3"
                          v-if="!props.item.discountedValue"
                        ></v-progress-circular>
                        <span v-else>{{
                          formatMoney(props.item.discountedValue)
                        }}</span>
                      </td>
                      <td
                        v-if="props.item.discountedValue"
                        class="text-xs-center"
                      >
                        <PaymentButton
                          :item="props.item"
                          :email="email"
                          :cellphone="cellphone"
                          :buttonClass="'to-expire-payment-slip__pay-btn financial--background'"
                          @openSlip="
                            OnPayToExpireSlip(selectedUnit, props.item)
                          "
                          :sendByEmail="sendSlipByEmailToExpire"
                          :sendBySms="sendSlipBySmsToExpire"
                        />
                      </td>
                    </template>
                  </v-data-table>
                </v-card-text>
              </v-card>
            </span>
          </template>
        </div>
        <div v-else class="empty-state">{{ $t('responses.error') }}</div>
      </v-flex>
    </v-layout>
  </module-page>
</template>

<script>
import bus from '@/utils/bus';
import enums from '@/utils/enums';
import ModulePage from '@/components/layout/Page';
import ScreenLoading from '@/components/loaders/ScreenLoading';
import EmptyStateCard from '@/components/layout/EmptyStateCard';
import PaymentButton from '@/components/financial/PaymentButton';
import toExpirePaymentSlipMixin from '@/mixins/modules/toExpirePaymentSlip';
import { APP, CONFIG, USER, TICKET_CRM } from '../../utils/constants';
export default {
  name: 'toExpirePaymentSlip',
  components: {
    ModulePage,
    ScreenLoading,
    EmptyStateCard,
    PaymentButton,
  },
  mixins: [toExpirePaymentSlipMixin],
  computed: {
    allowedContracts() {
      const { TO_EXPIRE_SLIPS } = enums.financialModules;
      return this.getAllowedContracts(
        TO_EXPIRE_SLIPS,
        this.selectedUnit.contracts
      );
    },
    imobmeUser() {
      return this.$store.getters[USER.GET_IMOBME_DATA];
    },
    ticketCrmEnabled() {
      return this.$store.getters[TICKET_CRM.GET_ENABLED];
    },
    emptyPaymentSlipsHtml() {
      return this.$t('pages.paymentSlip.emptyToExpireSlips', {
        extraInfo: this.hasFuturePaymentSlips
          ? this.$t('pages.paymentSlip.hasFutureToExpireSlips')
          : '',
      });
    },
    toExpireDays() {
      return this.$store.getters[CONFIG.GET_TO_EXPIRE_PERIOD_DAYS];
    },
  },
  data() {
    return {
      loaded: true,
      pageParams: this.$store.getters[APP.PAGES].paymentSlip,
      descriptionFirstLine: '',
      descriptionSecondLine: '',
      descriptionLink: '',
      headers: [
        {
          text: this.$t('pages.paymentSlip.expirationDate'),
          value: 'expirationDate',
          align: 'center',
        },
        {
          text: this.$t('pages.paymentSlip.discountedValue'),
          value: 'discountedValue',
          align: 'center',
        },
        { text: ' ', value: 'parcelCode', align: 'center' },
      ],
      email: '',
      cellphone: {
        countryCode: '+55',
        areaCode: '11',
        number: '',
      },
    };
  },
  created() {
    bus.$on('unit:change', this.getSlips);
    bus.$on('unit:update', this.getSlips);
    bus.$on('toExpireSlip:requestReady', this.update);
    this.email = this.user.email;
    if (this.imobmeUser && this.imobmeUser.cellphone) {
      const { number, areaCode, countryCode } = this.imobmeUser.cellphone;
      this.cellphone = { number, areaCode, countryCode: countryCode || '+55' };
    }
    this.descriptionFirstLine =
      this.pageParams.texts.toExpire.description.firstLine;
    this.descriptionSecondLine =
      this.pageParams.texts.toExpire.description.secondLine;
    this.descriptionLink = this.pageParams.texts.toExpire.description.link;
    this.getSlips();
  },
  beforeDestroy() {
    bus.$off('unit:change', this.getSlips);
    bus.$off('unit:update', this.getSlips);
    bus.$off('toExpireSlip:requestReady', this.update);
  },
  methods: {
    update() {
      this.loaded = false;
      this.$forceUpdate();
      this.$nextTick(function () {
        this.loaded = true;
      });
    },
    async getSlips(unitObj = null) {
      this.getToExpirePaymentSlips({ byContract: true });
    },
  },
};
</script>

<style lang="scss">
@import '../../assets/scss/breakpoint';
.waiting-state {
  &__title {
    margin: 0;
    font-weight: var(--font-weight);
  }
  &__text {
    margin: 0;
    font-size: 11px;
  }
}

.empty-state__text {
  a {
    color: var(--financial-color);
  }
}

.to-expire-payment-slip {
  &__description {
    font-size: 12px;
    padding-bottom: 10px;
    color: var(--label-color);
    a {
      color: var(--financial-color);
    }
    p {
      margin: 0;
    }
  }

  &__pay-btn {
    flex: 0.2;
    z-index: 1;
    height: 25px;
    min-width: 60px;
    font-size: 12px;
    margin: 0;
    padding: 5px 14px !important;
    border-radius: 5px;
    text-decoration: none;
    color: #fff;
    text-align: center;
    line-height: 25px;
  }
}

.payment-slip-link {
  cursor: pointer;
  font-weight: bold;
}
</style>



// WEBPACK FOOTER //
// src/views/financial/ToExpirePaymentSlip.vue